import { createAppRequest } from '@/services/request';
import { extractDetail } from '@/utils/apiResponse';
import type { CrmScopedViewMode } from '@/modules/zy-crm/utils/crm-view';
import type { ApiRequestOptions } from './types/common';
import type {
  ContractAiSummaryResult,
  ContractDrawerProfile,
  ContractDrawerSummaryPanel,
  ContractItem,
  ContractPageResult,
  ContractProjectionRepairResult,
  ContractQueryParams,
  ContractTypeItem,
  CreateContractDto,
  CreateContractTeamMemberDto,
  UpdateContractDto,
  UpsertContractAmountRatioDto,
} from './types/contract';

const request = createAppRequest();
const BASE_PATH = '/bff/zy-crm/contracts';

type DetailScopeOptions = ApiRequestOptions & {
  params?: Record<string, unknown>;
  viewMode?: CrmScopedViewMode;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object';

const normalizeContractPage = (resp: unknown): ContractPageResult => {
  if (!isObject(resp)) {
    throw new Error('合同列表返回结构异常，请检查 API 地址或代理配置');
  }
  const root = resp as Record<string, unknown>;
  const candidates: unknown[] = [
    root,
    root.data,
    root.result,
    root.payload,
    (root.response as Record<string, unknown> | undefined)?.data,
  ];
  for (const candidate of candidates) {
    if (!isObject(candidate)) {
      continue;
    }
    const pageObj = candidate as Record<string, unknown>;
    const hasList = Array.isArray(pageObj.list);
    const hasItems = Array.isArray(pageObj.items);
    const hasDataArray = Array.isArray(pageObj.data);
    const data: unknown[] = hasList
      ? (pageObj.list as unknown[])
      : hasItems
        ? (pageObj.items as unknown[])
        : hasDataArray
          ? (pageObj.data as unknown[])
          : [];
    const totalRaw = pageObj.total ?? pageObj.count ?? data.length;
    const pageRaw = pageObj.page ?? pageObj.current ?? 1;
    const pageSizeRaw =
      (pageObj.pageSize ?? pageObj.limit ?? data.length) || 20;
    if (
      hasList ||
      hasItems ||
      hasDataArray ||
      pageObj.total !== undefined ||
      pageObj.count !== undefined
    ) {
      return {
        list: data as ContractItem[],
        total: Number(totalRaw) || 0,
        page: Number(pageRaw) || 1,
        pageSize: Number(pageSizeRaw) || 20,
      };
    }
  }
  throw new Error('合同列表返回结构异常，请检查 API 地址或代理配置');
};

const unwrapContractDetail = (resp: unknown): ContractItem => {
  if (resp && typeof resp === 'object') {
    const obj = resp as Record<string, unknown>;
    const candidates = [
      obj,
      obj.data,
      obj.result,
      obj.payload,
      (obj.response as Record<string, unknown> | undefined)?.data,
    ];
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') {
        continue;
      }
      const record = candidate as Record<string, unknown>;
      if (typeof record.uuid === 'string') {
        return candidate as ContractItem;
      }
    }
  }
  throw new Error('合同详情返回结构异常');
};

export async function queryContracts(
  params: ContractQueryParams,
  options?: ApiRequestOptions,
) {
  const resp = await request<unknown>(BASE_PATH, {
    method: 'GET',
    params,
    ...(options || {}),
  });
  return normalizeContractPage(resp);
}

export async function queryContractDetail(
  uuid: string,
  options?: DetailScopeOptions,
) {
  const { viewMode, params, ...rest } = options || {};
  const resp = await request<unknown>(`${BASE_PATH}/${uuid}`, {
    method: 'GET',
    params: {
      ...(params || {}),
      ...(viewMode ? { viewMode } : {}),
    },
    ...rest,
  });
  return unwrapContractDetail(resp);
}

export async function queryContractDrawerProfile(
  uuid: string,
  options?: DetailScopeOptions,
) {
  const { viewMode, params, ...rest } = options || {};
  const resp = await request<ContractDrawerProfile | { data?: ContractDrawerProfile }>(
    `${BASE_PATH}/${uuid}/drawer-profile`,
    {
      method: 'GET',
      params: {
        ...(params || {}),
        ...(viewMode ? { viewMode } : {}),
      },
      ...rest,
    },
  );
  return extractDetail(resp) as ContractDrawerProfile;
}

export async function createContractTeamMember(
  uuid: string,
  payload: CreateContractTeamMemberDto,
  options?: ApiRequestOptions,
) {
  return request(`${BASE_PATH}/${uuid}/team-members`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function removeContractTeamMember(
  uuid: string,
  teamMemberUuid: string,
  options?: ApiRequestOptions,
) {
  return request(`${BASE_PATH}/${uuid}/team-members/${teamMemberUuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function upsertContractAmountRatio(
  uuid: string,
  payload: UpsertContractAmountRatioDto,
  options?: ApiRequestOptions,
) {
  return request(`${BASE_PATH}/${uuid}/amount-ratios`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function removeContractAmountRatio(
  uuid: string,
  amountRatioUuid: string,
  options?: ApiRequestOptions,
) {
  return request(`${BASE_PATH}/${uuid}/amount-ratios/${amountRatioUuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export { queryReceipts } from './receipt.api';
export { queryReceiptSchedules } from './receipt-schedule.api';

export async function queryContractTypes(params?: {
  pageNo?: number;
  pageSize?: number;
}) {
  const resp = await request<unknown>('/bff/zy-crm/contract-types', {
    method: 'GET',
    params,
  });
  const root =
    resp && typeof resp === 'object' ? (resp as Record<string, unknown>) : {};
  const data =
    root.item ||
    root.list ||
    (root.data && typeof root.data === 'object'
      ? (root.data as Record<string, unknown>).item ||
        (root.data as Record<string, unknown>).list
      : undefined);
  return (Array.isArray(data) ? data : []) as ContractTypeItem[];
}

export async function createContract(
  payload: CreateContractDto,
  options?: ApiRequestOptions,
) {
  return request<ContractItem>(BASE_PATH, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function updateContract(
  uuid: string,
  payload: UpdateContractDto,
  options?: ApiRequestOptions,
) {
  return request<ContractItem>(`${BASE_PATH}/${uuid}`, {
    method: 'PATCH',
    data: payload,
    ...(options || {}),
  });
}

export async function deleteContract(
  uuid: string,
  options?: ApiRequestOptions,
) {
  return request<void>(`${BASE_PATH}/${uuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function repairContractProjection(
  uuid: string,
  options?: ApiRequestOptions,
) {
  return request<ContractProjectionRepairResult>(
    `${BASE_PATH}/${uuid}/projection/repair`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
}
